import { useMutation, useQuery } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';

/** An organization in the ConsultNow V2 database. */
export interface SourceOrganization {
  id: string;
  name: string;
  patient_count: number;
}

/**
 * A patient in V2, with what importing them would bring across.
 *
 * `status` is decided against the organization being imported into:
 * - `new` — not here yet.
 * - `imported` — a patient with this id is already here.
 * - `conflict` — the medical record number belongs to a different chart here,
 *   so the import refuses until a strategy is chosen.
 */
export interface SourcePatient {
  id: string;
  medical_record_number: string;
  display_name: string;
  document_count: number;
  encounter_count: number;
  status: 'new' | 'imported' | 'conflict';
  conflict_with: string | null;
}

export type ImportStrategy = 'auto' | 'merge' | 'separate';

export interface ImportResult {
  patient_id: string;
  merged_into: string | null;
  objects_copied: number;
  rows: Record<string, number>;
}

/**
 * Whether this installation has a ConsultNow to import from, and whether the
 * signed-in user may use it. Answered for every signed-in user, so the
 * navigation can decide without a 403.
 */
export interface ImportAccess {
  configured: boolean;
  can_import: boolean;
  target_organization_id: string | null;
  target_organization_name: string | null;
}

export function useV2ImportAccess() {
  return useQuery({
    queryKey: ['v2-import', 'config'],
    queryFn: async () => {
      const { data } = await clientApi.get<ImportAccess>('/api/v1/v2-import/config');
      return data;
    },
    // Configuration, not data: it changes with a deploy, not during a session.
    staleTime: Infinity,
    retry: false,
  });
}

export function useSourceOrganizations(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['v2-import', 'organizations'],
    queryFn: async () => {
      const { data } = await clientApi.get<SourceOrganization[]>('/api/v1/v2-import/organizations');
      return data;
    },
    // The source is another product's live database; a stale list is worse
    // than a slow one, and this page is opened rarely.
    staleTime: 0,
  });
}

export function useSourcePatients(sourceOrgId: string | null) {
  return useQuery({
    queryKey: ['v2-import', 'patients', sourceOrgId],
    enabled: !!sourceOrgId,
    queryFn: async () => {
      const { data } = await clientApi.get<SourcePatient[]>(
        `/api/v1/v2-import/organizations/${sourceOrgId}/patients`,
      );
      return data;
    },
    staleTime: 0,
  });
}

/**
 * Imports ONE patient into the configured destination organization.
 *
 * The page calls this once per selected patient rather than sending the whole
 * selection, so each row reports its own outcome, a failure part-way through a
 * list does not lose what already succeeded, and each import stays inside one
 * short transaction on the server.
 */
export function useImportPatient() {
  return useMutation({
    mutationFn: async (input: { sourceOrgId: string; patientId: string; strategy?: ImportStrategy }) => {
      const { data } = await clientApi.post<ImportResult>(
        `/api/v1/v2-import/organizations/${input.sourceOrgId}/patients/${input.patientId}/import`,
        { strategy: input.strategy ?? 'auto' },
      );
      return data;
    },
  });
}
